import { useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CoverUpload } from './CoverUpload';
import { MarkdownPreview } from './MarkdownPreview';
import { createBookUrl } from '../../utils/slug';
import { useImageUpload } from '@/hooks/useImageUpload';
import type { PublishResult } from '@/types';

interface BookEditorProps {
  title: string;
  author: string;
  blurb: string;
  markdown: string;
  coverUrl: string;
  heroCoverUrl: string;
  error: string | null;
  publishResult: PublishResult | null;
  isPublishing: boolean;
  isEditMode?: boolean;
  onTitleChange: (value: string) => void;
  onAuthorChange: (value: string) => void;
  onBlurbChange: (value: string) => void;
  onMarkdownChange: (value: string) => void;
  onCoverUrlChange: (value: string) => void;
  onHeroCoverUrlChange: (value: string) => void;
  onLoadFromFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPublish: () => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}

export function BookEditor({
  title,
  author,
  blurb,
  markdown,
  coverUrl,
  heroCoverUrl,
  error,
  publishResult,
  isPublishing,
  isEditMode,
  onTitleChange,
  onAuthorChange,
  onBlurbChange,
  onMarkdownChange,
  onCoverUrlChange,
  onHeroCoverUrlChange,
  onLoadFromFile,
  onPublish,
  textareaRef: externalTextareaRef
}: BookEditorProps) {
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = externalTextareaRef || internalTextareaRef;
  
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [blurbTab, setBlurbTab] = useState<'edit' | 'preview'>('edit');
  
  const coverImageUpload = useImageUpload();
  const heroImageUpload = useImageUpload();

  const handleCoverDrop = useCallback(async (e: React.DragEvent) => {
    await coverImageUpload.handleDrop(e, title, (url, suggestedTitle) => {
      onCoverUrlChange(url);
      if (suggestedTitle) {
        onTitleChange(suggestedTitle);
      }
    });
  }, [coverImageUpload, title, onCoverUrlChange, onTitleChange]);

  const handleHeroDrop = useCallback(async (e: React.DragEvent) => {
    await heroImageUpload.handleDrop(e, title, (url) => {
      onHeroCoverUrlChange(url);
    });
  }, [heroImageUpload, title, onHeroCoverUrlChange]);

  return (
    <div className="space-y-6">
      {/* Title Area */}
      <div className="space-y-4">
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Book Title"
          className="w-full text-3xl font-semibold text-stone-800 placeholder:text-stone-300 bg-transparent border-none focus:outline-none focus:ring-0"
        />
        <div className="flex items-center gap-4">
          <span className="text-stone-400 text-sm">by</span>
          <input
            type="text"
            value={author}
            onChange={(e) => onAuthorChange(e.target.value)}
            placeholder="Author Name"
            className="flex-1 text-lg text-stone-600 placeholder:text-stone-300 bg-transparent border-none focus:outline-none focus:ring-0"
          />
        </div>
      </div>

      {/* Cover Uploads - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CoverUpload
          coverUrl={coverUrl}
          isDragging={coverImageUpload.isDragging}
          isUploading={coverImageUpload.isUploading}
          label="Cover (with text)"
          description="Drop cover with text"
          aspectRatio="2/3"
          onDragOver={coverImageUpload.handleDragOver}
          onDragLeave={coverImageUpload.handleDragLeave}
          onDrop={handleCoverDrop}
          onRemove={() => onCoverUrlChange('')}
        />

        <CoverUpload
          coverUrl={heroCoverUrl}
          isDragging={heroImageUpload.isDragging}
          isUploading={heroImageUpload.isUploading}
          label="Hero Image (no text)"
          description="Drop hero image"
          aspectRatio="2/3"
          onDragOver={heroImageUpload.handleDragOver}
          onDragLeave={heroImageUpload.handleDragLeave}
          onDrop={handleHeroDrop}
          onRemove={() => onHeroCoverUrlChange('')}
        />
      </div>

      {/* Blurb with Tabs */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-stone-600">Short Description</label>
          <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setBlurbTab('edit')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                blurbTab === 'edit'
                  ? 'bg-white text-stone-800 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Edit
            </button>
            <button
              onClick={() => setBlurbTab('preview')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                blurbTab === 'preview'
                  ? 'bg-white text-stone-800 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Preview
            </button>
          </div>
        </div>
        
        {blurbTab === 'edit' ? (
          <textarea
            value={blurb}
            onChange={(e) => onBlurbChange(e.target.value)}
            placeholder="Write a short description... (supports **bold**, *italic*, [links](url))"
            rows={3}
            className="w-full px-4 py-3 text-stone-700 placeholder:text-stone-300 bg-transparent border-none focus:outline-none resize-none font-mono text-sm leading-relaxed"
          />
        ) : (
          <div className="w-full px-4 py-3 bg-stone-50 rounded-lg border border-stone-200 min-h-[80px]">
            <MarkdownPreview markdown={blurb} />
          </div>
        )}
      </div>

      {/* Editor Toolbar */}
      <div className="flex items-center justify-between py-2 border-y border-stone-200">
        <div className="flex items-center gap-2">
          <label className="text-xs text-stone-500 hover:text-stone-700 px-3 py-1.5 rounded hover:bg-stone-100 transition-colors cursor-pointer">
            Import .md
            <input type="file" accept=".md,.txt" onChange={onLoadFromFile} className="hidden" />
          </label>
          <span className="text-xs text-stone-400">|</span>
          <span className="text-xs text-stone-400">Supports **bold**, *italic*, [links](url)</span>
        </div>
        <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'edit'
                ? 'bg-white text-stone-800 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Edit
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'preview'
                ? 'bg-white text-stone-800 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Preview
          </button>
        </div>
      </div>

      {/* Markdown Editor / Preview */}
      {activeTab === 'edit' ? (
        <textarea
          ref={textareaRef}
          value={markdown}
          onChange={(e) => onMarkdownChange(e.target.value)}
          placeholder={`# Chapter 1

Write your story here... Use **bold**, *italic*, or [links](https://example.com)

Add audio with: [audio:id|label]`}
          className="w-full min-h-[500px] px-4 py-4 text-stone-700 placeholder:text-stone-300 bg-transparent border-none focus:outline-none resize-none font-mono text-sm leading-relaxed"
        />
      ) : (
        <div className="w-full min-h-[500px] px-4 py-4 bg-stone-50 rounded-lg border border-stone-200 overflow-auto">
          <MarkdownPreview markdown={markdown} />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <div className="text-sm">
          {error && <span className="text-red-600">{error}</span>}
          {publishResult && (
            <span className="text-teal-600">
              Published! <Link to={createBookUrl(title, publishResult.bookId)} className="underline">View book</Link>
            </span>
          )}
        </div>
        <button
          onClick={onPublish}
          disabled={isPublishing || !title || !author || !markdown}
          className="px-6 py-2.5 bg-stone-800 hover:bg-stone-900 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-all"
        >
          {isPublishing ? (isEditMode ? 'Saving...' : 'Publishing...') : (isEditMode ? 'Save Changes' : 'Publish Book')}
        </button>
      </div>
    </div>
  );
}
